import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import ProductEditor from '../components/admin/ProductEditor';
import '../styles/Admin.css';

const AdminProductForm = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [product, setProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await apiClient.get(`/products/${id}`);
      setProduct(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product details');
      navigate('/admin');
    }
  };

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    try {
      setUploading(true);
      const token = sessionStorage.getItem('adminToken');
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('images', f));
      const res = await apiClient.post('/upload/images', fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const urls = res.data.urls || [];

      // Update local product state with new images immediately
      setProduct(prev => ({
        ...prev,
        images: [...(prev?.images || []), ...urls]
      }));
    } catch (e) {
      console.error('Upload failed', e);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        ...formData,
        price: Number(formData.price),
        originalPrice: formData.originalPrice ? Number(formData.originalPrice) : undefined,
        discount: Number(formData.discount),
        stockQuantity: Number(formData.stockQuantity),
        lowStockThreshold: Number(formData.lowStockThreshold),
        weight: formData.weight ? Number(formData.weight) : undefined,
        dimensions: {
          length: formData.dimensions?.length ? Number(formData.dimensions.length) : undefined,
          width: formData.dimensions?.width ? Number(formData.dimensions.width) : undefined,
          height: formData.dimensions?.height ? Number(formData.dimensions.height) : undefined,
        },
        // Ensure backward compatibility
        image: formData.images[0] || '',
        images: formData.images
      };

      const token = sessionStorage.getItem('adminToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (isEditMode) {
        await apiClient.put(`/products/${id}`, payload, config);
      } else {
        await apiClient.post('/products', payload, config);
      }
      navigate('/admin');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  if (loading) return <div className="admin-container">Loading...</div>;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
      </div>

      <ProductEditor
        product={product}
        onSave={handleSave}
        onCancel={handleCancel}
        uploading={uploading}
        onUpload={handleUploadFiles}
      />
    </div>
  );
};

export default AdminProductForm;
